import type { RaterXPreset } from '../api/types/rater-x-preset';
import type { PrimeXQuoteState } from '../types';
import type { CustomizeQuoteState } from '../views/customize-quote/types';
import type { Nullable } from '../../../../../types/common/utility';
import type { RaterResponse } from '../../../../agencies/quotes/PrimeX/quoteDetails/hooks/useGetQuotePageDetails';
import { flattenModifiers } from './uw/ratingOptions';

export const formatCoveragesForState = (
  coverageCategories: RaterXPreset['coverageCategories']
) => {
  const _flattenedCoverages = flattenCoverages(coverageCategories);

  return _flattenedCoverages.reduce(
    (acc: PrimeXQuoteState['coverageState'], coverage) => {
      return {
        ...acc,
        [coverage.key]: coverage,
      };
    },
    {}
  );
};

const mergeCoveragesIntoState = <T extends CustomizeQuoteState>(data: T) => {
  const coverageState = formatCoveragesForState(data.raterX.coverageCategories);

  return {
    ...data,
    coverageState,
  };
};

export default mergeCoveragesIntoState;

export function flattenCoverages(
  data: RaterXPreset['coverageCategories'] | undefined
) {
  if (!data) {
    return [];
  }

  type Coverage =
    RaterXPreset['coverageCategories'][0]['groups'][0]['coverages'][0];

  const collectCoverages = (coverages: Coverage[]): Coverage[] =>
    coverages.flatMap((coverage) => [
      coverage,
      ...collectCoverages(coverage.children ?? []),
    ]);

  return data.reduce((acc, category) => {
    return category.groups.reduce((groupAcc, group) => {
      return [...groupAcc, ...acc, ...collectCoverages(group.coverages)];
    }, [] as Coverage[]);
  }, [] as Coverage[]);
}

export function buildRaterItemFieldIds(
  raterResponse: Nullable<RaterResponse>
): Set<string> {
  const coverages = flattenCoverages(raterResponse?.coverageCategories) ?? [];
  const modifiers =
    flattenModifiers(raterResponse?.rateModifierCategories) ?? [];
  return new Set(
    [...coverages, ...modifiers]
      .map((item) => item?.field?.id)
      .filter(Boolean) as string[]
  );
}
